/**
 * The main run surface: pick a connection, pick an action, fill the generated
 * form, see the response. Or drop to the Any endpoint tab and compose a request
 * by hand against the same authorized connection.
 *
 * Nothing here knows the name of a single connector.
 */

import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useActionSchema, useExecuteAction } from '@/core/hooks'
import { useConnection } from '@/core/connection/useConnection'
import { ConnectionPicker } from '@/core/connection/ConnectionPicker'
import { useConnections } from '@/core/connection/connection.context'
import { SchemaForm } from '@/core/schema-renderer/SchemaForm'
import { formatError } from '@/core/utils/error.formatter'
import {
  Callout,
  EmptyState,
  ErrorNotice,
  Loading,
  Logo,
  MethodTag,
} from '@/shared/components'
import { ActionList } from './ActionList'
import { ActionResult } from './ActionResult'
import { CustomRequestPanel } from './CustomRequestPanel'
import { Constellation } from '@/shared/components/Constellation'

type Tab = 'actions' | 'custom'

export function ActionRunnerPage() {
  const [params, setParams] = useSearchParams()
  const { connections, isLoading: loadingConnections } = useConnections()

  const connectionId = params.get('connection')
  const actionId = params.get('action')
  const tab = (params.get('tab') as Tab) || 'actions'

  const { connection, actions, isLoading: loadingActions } = useConnection(connectionId)
  const schema = useActionSchema(connection?.connector_id, actionId)
  const execute = useExecuteAction(connectionId, actionId)

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value === null) next.delete(key)
    else next.set(key, value)
    // Changing the connection invalidates the chosen action: a GitHub action id
    // is meaningless on a Slack connection.
    if (key === 'connection') next.delete('action')
    setParams(next, { replace: true })
  }

  // Reset the last result when the target changes, so a stale response is never
  // shown under a different action's form.
  useEffect(() => {
    execute.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, actionId])

  // With exactly one connection, preselecting it removes a pointless click.
  useEffect(() => {
    if (!connectionId && connections.length === 1) {
      setParam('connection', connections[0].connection_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections.length, connectionId])

  if (loadingConnections) return <div className="page"><Loading /></div>

  if (connections.length === 0) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Run an action</h1>
        </div>
        <EmptyState
          title="No connections yet"
          action={
            <Link className="btn btn-primary" to="/catalog">
              Browse the catalog
            </Link>
          }
        >
          Connect an app first. A database connection needs nothing but its host
          and a password, so you can be running live queries in a minute.
        </EmptyState>
      </div>
    )
  }

  const serverFieldErrors = execute.error ? formatError(execute.error).fieldErrors : undefined

  return (
    <div className="page">
      <div className="page-header hero">
        <Constellation />
        <div className="hero-text">
          <h1>Run an action</h1>
          <p>
            Pick a connection, then run any action its connector declares — or use
            Any endpoint to call something the manifests do not cover.
          </p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <ConnectionPicker
          value={connectionId}
          onChange={(id) => setParam('connection', id)}
        />
      </div>

      {connection && (
        <>
          <div className="row" style={{ marginBottom: 12, gap: 10 }}>
            <Logo
              src={connection.connector_logo}
              name={connection.connector_name ?? connection.connector_id}
              id={connection.connector_id}
              small
            />
            <strong>{connection.connector_name}</strong>
            <Link
              className="small"
              to={`/connections/${connection.connection_id}`}
            >
              connection details
            </Link>
          </div>

          {connection.status !== 'active' && (
            <Callout kind="warn" title={`This connection is ${connection.status}`}>
              <p className="small">
                {connection.last_error ??
                  'Requests will probably fail until it is reconnected.'}
              </p>
            </Callout>
          )}

          <div className="tabs" style={{ marginTop: 14 }}>
            <button
              className="tab"
              data-on={tab === 'actions'}
              onClick={() => setParam('tab', 'actions')}
            >
              Declared actions
            </button>
            <button
              className="tab"
              data-on={tab === 'custom'}
              onClick={() => setParam('tab', 'custom')}
            >
              Any endpoint
            </button>
          </div>

          {tab === 'custom' ? (
            <CustomRequestPanel connectionId={connection.connection_id} />
          ) : (
            <div className="split">
              <div className="stack">
                <h2>Actions</h2>
                {loadingActions ? (
                  <Loading label="Loading actions…" />
                ) : (
                  <ActionList
                    actions={actions}
                    selected={actionId}
                    onSelect={(id) => setParam('action', id)}
                  />
                )}
              </div>

              <div className="stack">
                {!actionId && (
                  <div className="card">
                    <EmptyState title="Choose an action">
                      Its form is generated from the action's input schema — every
                      field, dropdown and validation rule comes from the backend.
                    </EmptyState>
                  </div>
                )}

                {actionId && schema.isLoading && (
                  <div className="card">
                    <Loading label="Reading the action schema…" />
                  </div>
                )}

                {actionId && schema.error && (
                  <ErrorNotice error={schema.error} onRetry={() => schema.refetch()} />
                )}

                {schema.data && (
                  <>
                    <div className="card stack" style={{ gap: 12 }}>
                      <div>
                        <div className="row-tight">
                          <MethodTag method={schema.data.method} />
                          <h2>{schema.data.name}</h2>
                        </div>
                        <p className="small muted" style={{ margin: '5px 0 0' }}>
                          {schema.data.description}
                        </p>
                        {schema.data.required_scopes.length > 0 && (
                          <p className="small faint mono" style={{ margin: '6px 0 0' }}>
                            needs: {schema.data.required_scopes.join(', ')}
                          </p>
                        )}
                      </div>

                      <SchemaForm
                        cacheKey={`${schema.data.connector_id}:${schema.data.id}`}
                        schema={schema.data.input_schema}
                        connectionId={connection.connection_id}
                        busy={execute.isPending}
                        serverErrors={serverFieldErrors}
                        onSubmit={(input) => execute.mutate(input)}
                        onReset={() => execute.reset()}
                        footerExtra={
                          schema.data.paginated ? (
                            <span className="small faint">
                              pages are followed automatically
                            </span>
                          ) : undefined
                        }
                      />
                    </div>

                    {execute.error && <ErrorNotice error={execute.error} />}

                    {execute.data && (
                      <div className="card">
                        <h3 style={{ marginBottom: 10 }}>Response</h3>
                        <ActionResult result={execute.data} schema={schema.data} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
